import React, { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addDays, isToday, isBefore, isAfter } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface BookingCalendarProps {
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
  maxAdvanceBookingDays?: number;
  lockedDates?: string[];
  fullyBookedDates?: string[];
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({ 
  onDateSelect, 
  selectedDate, 
  maxAdvanceBookingDays = 90,
  lockedDates = [],
  fullyBookedDates = []
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = new Date();
  const maxDate = addDays(today, maxAdvanceBookingDays);

  const prevMonth = () => {
    const newDate = subMonths(currentMonth, 1);
    if (!isBefore(endOfMonth(newDate), today)) {
      setCurrentMonth(newDate);
    }
  };

  const nextMonth = () => {
    const newDate = addMonths(currentMonth, 1);
    if (!isAfter(startOfMonth(newDate), maxDate)) {
      setCurrentMonth(newDate);
    }
  };

  const renderHeader = () => {
    const prevMonthDate = subMonths(currentMonth, 1);
    const nextMonthDate = addMonths(currentMonth, 1);
    
    return (
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={prevMonth}
          disabled={isBefore(endOfMonth(prevMonthDate), today)}
          className="p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-bold">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          onClick={nextMonth}
          disabled={isAfter(startOfMonth(nextMonthDate), maxDate)}
          className="p-2 rounded-full text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  };

  const renderDays = () => {
    const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    return (
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map((day) => (
          <div
            key={day}
            className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2"
          >
            {day}
          </div>
        ))}
      </div>
    );
  };

  const isDateLocked = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    for (const period of lockedDates) {
      const [start, end] = period.split('|');
      if (start && end) {
        const startDate = new Date(start + 'T00:00:00');
        const endDate = new Date(end + 'T23:59:59');
        if (date >= startDate && date <= endDate) {
          return true;
        }
      }
    }
    return false;
  };

  const isDateFullyBooked = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return fullyBookedDates.includes(dateStr);
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        const isPast = isBefore(day, today) && !isToday(day);
        const isSelected = selectedDate && isSameDay(day, selectedDate);
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isFuture = isAfter(day, maxDate);
        const locked = isDateLocked(day);
        const fullyBooked = isDateFullyBooked(day);
        
        const isDisabled = isPast || !isCurrentMonth || isFuture || locked || fullyBooked;
        
        let cellClass = 'relative p-2 min-h-[60px] w-full rounded-md transition-colors duration-200 ';
        
        if (!isCurrentMonth) {
          cellClass += 'text-gray-300 dark:text-gray-600 cursor-not-allowed ';
        } else if (isDisabled) {
          if (locked) {
            cellClass += 'bg-red-50 dark:bg-red-900/20 text-red-300 dark:text-red-500 cursor-not-allowed border border-red-200 dark:border-red-800 ';
          } else if (fullyBooked) {
            cellClass += 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed text-gray-400 dark:text-gray-500 ';
          } else {
            cellClass += 'text-gray-300 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50 ';
          }
        } else {
          cellClass += 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer ';
        }
        
        if (isSelected) {
          cellClass += 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500 ';
        }
        
        if (isToday(day) && !isSelected && !isDisabled) {
          cellClass += 'border border-primary-500 ';
        }
        
        days.push(
          <button
            key={day.toString()}
            onClick={() => {
              if (!isDisabled) {
                onDateSelect(cloneDay);
              }
            }}
            disabled={isDisabled}
            className={cellClass}
            aria-label={format(day, 'PPPP')}
            aria-pressed={isSelected}
            title={locked ? 'Date not available' : fullyBooked ? 'Fully booked' : ''}
          >
            <span className={`block text-right ${isSelected ? 'font-bold' : ''}`}>
              {format(day, 'd')}
            </span>
            
            {locked && (
              <span className="absolute bottom-1 left-1 text-xs text-red-400">🔒</span>
            )}
            {fullyBooked && !locked && (
              <span className="absolute bottom-1 left-1 text-xs text-gray-400">✕</span>
            )}
            
            {isToday(day) && !isSelected && !isDisabled && (
              <span className="absolute bottom-1 left-1 w-2 h-2 bg-primary-500 rounded-full"></span>
            )}
          </button>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7 gap-1">
          {days}
        </div>
      );
      days = [];
    }
    return <div className="space-y-1">{rows}</div>;
  };

  return (
    <div className="card p-4">
      {renderHeader()}
      {renderDays()}
      {renderCells()}
    </div>
  );
};

export default BookingCalendar;
